import handleHtmlData from 'utils/global/handleHtmlData'

/**
 * What `updateDynamicText` reads for one box's HTML. A case write that leaves
 * every listed input unchanged cannot change the box's text, so the box can
 * skip the re-apply.
 */
export type DynamicTextDependencies = {
  caseKeys: readonly string[]
  serviceIds: readonly string[]
  usesDefaultService: boolean
  usesDefaultVisitation: boolean
  usesFuneralHome: boolean
  hasDynamicText: boolean
}

export type DynamicTextInputs = {
  caseDetails: Record<string, unknown> | null | undefined
  funeralHomeDetails: Record<string, unknown> | null | undefined
  dateFormat: string | null | undefined
}

export const NO_DYNAMIC_TEXT: DynamicTextDependencies = Object.freeze({
  caseKeys: Object.freeze([]) as readonly string[],
  serviceIds: Object.freeze([]) as readonly string[],
  usesDefaultService: false,
  usesDefaultVisitation: false,
  usesFuneralHome: false,
  hasDynamicText: false,
})

const FUNERAL_HOME_PATH_ROOT = 'funeralHomeDetails'
const FIRST_LETTER_PREFIX = 'first_letter_'
const SERVICE_PATH_ROOTS = new Set([
  'services',
  'default_service',
  'default_visitation',
])
const TEMPLATE_CASE_KEYS: Record<string, readonly string[]> = {
  date_of_birth_year: ['date_of_birth'],
  date_of_death_year: ['date_of_death'],
  final_resting: [
    'interment_place',
    'interment_address',
    'interment_city',
    'interment_state',
    'interment_postal_code',
  ],
  memorial_contributions: ['memorial_contributions'],
}
const DEFAULT_SERVICE_TEMPLATE_KEYS = new Set(['service', 'default_service'])
const SPECIFIC_SERVICE_TEMPLATE_KEY = 'specific_service'
const VISITATION_TEMPLATE_KEY = 'visitation'
const MADE_IN_TEMPLATE_KEY = 'made_in'
const HONORARY_PALLBEARERS_PATH = 'honorary_pallbearers'
const PALLBEARER_RELATION_KEY = 'pallbearers'
const NAME_OF_DECEASED_PATH = 'name_of_deceased'
const NAME_ARRANGEMENT_KEY = 'name_arrangement'

const TAG_PATTERN = /<[a-zA-Z][^>]*>/g
const ATTRIBUTE_PATTERN =
  /\b(data-case-detail-table|data-case-detail|data-template|data-properties|id)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g

type TagAttributes = Partial<
  Record<
    | 'data-case-detail-table'
    | 'data-case-detail'
    | 'data-template'
    | 'data-properties'
    | 'id',
    string
  >
>

const attributesOf = (tag: string): TagAttributes => {
  const attributes: TagAttributes = {}
  for (const match of tag.matchAll(ATTRIBUTE_PATTERN)) {
    const name = match[1] as keyof TagAttributes
    if (attributes[name] !== undefined) continue
    attributes[name] = match[2] ?? match[3] ?? match[4] ?? ''
  }
  return attributes
}

const topLevelKey = (path: string): string => path.split(/[.[]/, 1)[0] ?? ''

const serviceIdFromProperties = (
  encoded: string | undefined,
): string | null => {
  if (!encoded) return null
  const decoded = handleHtmlData.decode(encoded) as {
    serviceId?: unknown
  } | null
  const serviceId = decoded?.serviceId
  return typeof serviceId === 'string' && serviceId !== '' ? serviceId : null
}

class DependencyCollector {
  caseKeys = new Set<string>()
  serviceIds = new Set<string>()
  usesDefaultService = false
  usesDefaultVisitation = false
  usesFuneralHome = false
  hasDynamicText = false

  addCaseDetailPath(rawPath: string): void {
    this.hasDynamicText = true
    const path = rawPath.split(' ', 1)[0] ?? ''
    if (path === '') return
    const key = topLevelKey(
      path.startsWith(FIRST_LETTER_PREFIX)
        ? path.slice(FIRST_LETTER_PREFIX.length)
        : path,
    )
    if (key === FUNERAL_HOME_PATH_ROOT) {
      this.usesFuneralHome = true
      return
    }
    if (SERVICE_PATH_ROOTS.has(key)) {
      for (const root of SERVICE_PATH_ROOTS) this.caseKeys.add(root)
      return
    }
    this.caseKeys.add(key)
    if (key === HONORARY_PALLBEARERS_PATH)
      this.caseKeys.add(PALLBEARER_RELATION_KEY)
    if (key === NAME_OF_DECEASED_PATH) this.caseKeys.add(NAME_ARRANGEMENT_KEY)
  }

  addTemplate(key: string, properties: string | undefined): void {
    this.hasDynamicText = true
    if (key.toLowerCase().endsWith('header')) {
      this.caseKeys.add('case_preferences')
      return
    }
    if (DEFAULT_SERVICE_TEMPLATE_KEYS.has(key)) {
      this.usesDefaultService = true
      return
    }
    if (key === VISITATION_TEMPLATE_KEY) {
      this.usesDefaultVisitation = true
      return
    }
    if (key === SPECIFIC_SERVICE_TEMPLATE_KEY) {
      const serviceId = serviceIdFromProperties(properties)
      if (serviceId === null) this.caseKeys.add('services')
      else this.serviceIds.add(serviceId)
      return
    }
    if (key === MADE_IN_TEMPLATE_KEY) {
      this.usesFuneralHome = true
      return
    }
    for (const caseKey of TEMPLATE_CASE_KEYS[key] ?? [])
      this.caseKeys.add(caseKey)
  }

  toDependencies(): DynamicTextDependencies {
    if (!this.hasDynamicText) return NO_DYNAMIC_TEXT
    return {
      caseKeys: Array.from(this.caseKeys).sort(),
      serviceIds: Array.from(this.serviceIds).sort(),
      usesDefaultService: this.usesDefaultService,
      usesDefaultVisitation: this.usesDefaultVisitation,
      usesFuneralHome: this.usesFuneralHome,
      hasDynamicText: true,
    }
  }
}

export const dynamicTextDependencies = (
  html: string | null | undefined,
): DynamicTextDependencies => {
  if (!html) return NO_DYNAMIC_TEXT
  const collector = new DependencyCollector()
  for (const [tag] of html.matchAll(TAG_PATTERN)) {
    const attributes = attributesOf(tag)
    if (attributes.id === 'service-information-template')
      collector.hasDynamicText = true
    const caseDetailPath =
      attributes['data-case-detail'] ?? attributes['data-case-detail-table']
    if (caseDetailPath !== undefined)
      collector.addCaseDetailPath(caseDetailPath)
    const templateKey = attributes['data-template']
    if (templateKey !== undefined)
      collector.addTemplate(templateKey, attributes['data-properties'])
  }
  return collector.toDependencies()
}

type ServiceRow = { id?: unknown; default?: unknown; type?: unknown }

const servicesOf = (
  caseDetails: DynamicTextInputs['caseDetails'],
): ServiceRow[] => {
  const services = caseDetails?.services
  return Array.isArray(services) ? (services as ServiceRow[]) : []
}

const defaultServiceOfType = (
  services: readonly ServiceRow[],
  type: 'service' | 'visitation',
): ServiceRow | null =>
  services.find(
    (row) =>
      row.default === true &&
      typeof row.type === 'string' &&
      row.type.toLowerCase() === type,
  ) ?? null

/**
 * One string per box that changes exactly when an input `updateDynamicText`
 * would read for that box changes. The case is spelled out column by column
 * rather than by object identity because a case write replaces the whole
 * object even when it touched one column.
 */
export const dynamicTextInputsSignature = (
  inputs: DynamicTextInputs,
  dependencies: DynamicTextDependencies,
): string => {
  if (!dependencies.hasDynamicText) return ''
  const { caseDetails, funeralHomeDetails, dateFormat } = inputs
  const services = servicesOf(caseDetails)
  const parts: unknown[] = [dateFormat ?? null]
  for (const key of dependencies.caseKeys)
    parts.push(caseDetails?.[key] ?? null)
  for (const serviceId of dependencies.serviceIds)
    parts.push(services.find((row) => row.id === serviceId) ?? null)
  if (dependencies.usesDefaultService)
    parts.push(defaultServiceOfType(services, 'service'))
  if (dependencies.usesDefaultVisitation)
    parts.push(defaultServiceOfType(services, 'visitation'))
  if (dependencies.usesFuneralHome) parts.push(funeralHomeDetails ?? null)
  return JSON.stringify(parts)
}
